import type { OperationsAttentionKey, OperationsAttentionRowDto, OperationsLensKey, OperationsPenKey } from "@/api/endpoints";
import { InfoTooltip } from "@/components/InfoTooltip";
import { ATTENTION_CLASS, ATTENTION_LABEL, ATTENTION_ORDER, attentionTip, deltaClass, formatDelta } from "../operationsLabels";
import { UnlensedTag } from "./LensTags";

interface Props {
  openNow: number;
  states: OperationsAttentionRowDto[];
  stuckAfterBd: number;
  blockedAfterBd: number;
  active?: OperationsAttentionKey;
  /** The lens in force: that tile reads as the lens control, the others dim (they still count the full population). */
  lens?: OperationsLensKey;
  /** The pen lens never cuts the tiles (they count every open item): each tile says so while it is on. */
  pen?: OperationsPenKey;
  onDrill: (state: OperationsAttentionKey, stuckBy?: "workOrder") => void;
  /** Toggles the lens from a tile's footer link. */
  onLens?: (state: OperationsLensKey | undefined) => void;
}

/**
 * Panel 4: Stuck, Blocked, Stuck + Blocked and Clear (plus Unknown when activity rows were
 * unavailable). One exclusive state per request from the two clocks, so the tiles add up to Open now.
 */
export function AttentionTiles({ openNow, states, stuckAfterBd, blockedAfterBd, active, lens, pen, onDrill, onLens }: Props) {
  const byState = new Map(states.map((s) => [s.state, s]));
  const unknown = byState.get("unknown");
  const order: OperationsAttentionKey[] = unknown && unknown.count > 0 ? [...ATTENTION_ORDER, "unknown"] : ATTENTION_ORDER;

  return (
    <section className="ops-span-12" aria-label="Attention">
      <div className={`ops-tiles${order.length >= 4 ? " four" : ""}`}>
        {order.map((key) => {
          const row = byState.get(key);
          const count = row?.count ?? 0;
          const delta = row?.delta ?? 0;
          const fromWorkOrder = key === "clear" || key === "unknown" ? 0 : (row?.fromWorkOrder ?? 0);
          const pct = openNow ? ((count / openNow) * 100).toFixed(1) : "0.0";
          // For Clear, growth is good; for the other states, growth is bad.
          const tone = key === "clear" ? (delta > 0 ? "good" : delta < 0 ? "bad" : "flat") : (delta > 0 ? "bad" : delta < 0 ? "good" : "flat");
          return (
            <button
              key={key}
              type="button"
              className={`ops-tile ${ATTENTION_CLASS[key]}${active === key || lens === key ? " active" : ""}${(lens && lens !== key) || (pen && !lens) ? " ops-unlensed" : ""}`}
              onClick={() => onDrill(key)}
              aria-pressed={active === key}
              aria-label={`${ATTENTION_LABEL[key]}: ${count} open`}
            >
              <span className="lbl">
                {ATTENTION_LABEL[key]}
                <InfoTooltip inline text={attentionTip(key, stuckAfterBd, blockedAfterBd)} />
                {((lens && lens !== key) || (pen && !lens)) && <UnlensedTag />}
              </span>
              <span className="val">{count}</span>
              <span className="sub">
                <span>{pct}% of open</span>
                <span aria-hidden="true">·</span>
                <span className={`ops-delta ${deltaClass(delta)} ${tone}`}>{formatDelta(delta)} DoD</span>
                {fromWorkOrder > 0 && (
                  <>
                    <span aria-hidden="true">·</span>
                    <span
                      className="ops-tile-split"
                      role="link"
                      tabIndex={0}
                      title="Waiting on the work order (to submit or to approve), not on a task. Click to list them."
                      onClick={(e) => { e.stopPropagation(); onDrill(key, "workOrder"); }}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onDrill(key, "workOrder"); } }}
                    >
                      {fromWorkOrder} by work order
                    </span>
                  </>
                )}
                {onLens && key !== "unknown" && (
                  <>
                    <span aria-hidden="true">·</span>
                    <span
                      className="ops-tile-split ops-tile-lens"
                      role="link"
                      tabIndex={0}
                      title={lens === key ? "Remove the lens." : `Show only ${ATTENTION_LABEL[key]} items on the cards below.`}
                      onClick={(e) => { e.stopPropagation(); onLens(lens === key ? undefined : key); }}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onLens(lens === key ? undefined : key); } }}
                    >
                      {lens === key ? "Lens on · remove" : "Lens"}
                    </span>
                  </>
                )}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
